import { type ReactNode, useEffect, useRef, useSyncExternalStore } from 'react';
import type { KeyboardShortcut } from '@/lib/platform';

/*
 * What ⌘K finds on the page shown, besides conversations, artifacts and commands (D21): a library
 * page or an app declares its items; ⌘K or a sidebar header's search button opens the one search
 * dialog (`components/sidebar/search-dialog`). Pages mount after the shell, so the last mounted
 * source wins.
 */

/** Toggles the search dialog, from anywhere (⌘K, Ctrl+K). */
export const SIDEBAR_SEARCH_SHORTCUT: KeyboardShortcut = { key: 'k', commandOrControl: true };

type SidebarSearchItem = {
  id: string;
  label: string;
  icon?: ReactNode;
  /** Shown on the right, muted (a project, a type). */
  meta?: string;
  /** Shown at the far right (an age). */
  shortcut?: string;
  /** More text the search matches. */
  keywords?: readonly string[];
  current?: boolean;
};

export type SidebarSearchSource = {
  title: string;
  placeholder: string;
  empty: string;
  groups: readonly { heading: string; items: readonly SidebarSearchItem[] }[];
  onSelect: (item: SidebarSearchItem) => void;
};

type Entry = { source: SidebarSearchSource | null; background: boolean };

// In mount order; a source keeps its place when its content changes.
const entries: Entry[] = [];
let open = false;
const listeners = new Set<() => void>();
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
let snapshot: { source: SidebarSearchSource | null; open: boolean } = { source: null, open };
const refresh = () => {
  const candidates = entries.filter((entry) => entry.source !== null);
  const source =
    (candidates.findLast((entry) => !entry.background) ?? candidates.at(-1))?.source ?? null;
  const next = { source, open };
  if (next.source === snapshot.source && next.open === snapshot.open) return;
  snapshot = next;
  for (const listener of listeners) listener();
};

/**
 * Declares what the finder searches while this component is mounted. A `background` source (the
 * conversations) gives way to any page's.
 */
export function useSidebarSearchSource(
  source: SidebarSearchSource | null,
  { background = false }: { background?: boolean } = {},
): void {
  const entry = useRef<Entry | null>(null);
  useEffect(() => {
    const own: Entry = { source: null, background };
    entry.current = own;
    entries.push(own);
    return () => {
      entries.splice(entries.indexOf(own), 1);
      entry.current = null;
      refresh();
    };
  }, [background]);
  useEffect(() => {
    if (entry.current === null) return;
    entry.current.source = source;
    refresh();
  }, [source]);
}

export function setSidebarSearchOpen(next: boolean | ((current: boolean) => boolean)): void {
  open = typeof next === 'function' ? next(open) : next;
  refresh();
}

/** The page's source and whether the search dialog is open. */
export function useSidebarSearch(): { source: SidebarSearchSource | null; open: boolean } {
  return useSyncExternalStore(
    subscribe,
    () => snapshot,
    () => snapshot,
  );
}
